import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useTranslation } from '../locales/useTranslation'
import Button from '../components/shared/Button'

const ROLE_LABELS = {
  rijder: '🚗 EV-rijder',
  eigenaar: '🏠 Laadpaal eigenaar',
  beide: '⚡ Rijder & Eigenaar',
}

const ProfilePage = ({ session, showToast, onShowOnboarding, onLogout }) => {
  const { t } = useTranslation()
  const [profile, setProfile] = useState(null)
  const [stats, setStats] = useState({ bookings: 0, chargers: 0, accepted: 0 })
  const [loading, setLoading] = useState(true)
  const [showLogout, setShowLogout] = useState(false)

  const fetchProfile = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .single()
      if (error) throw error
      setProfile(data)
    } catch (error) {
      showToast(t('fout'), true)
    } finally {
      setLoading(false)
    }
  }, [session.user.id, showToast, t])

  const fetchStats = useCallback(async () => {
    try {
      const [
        { count: bookingCount },
        { count: chargerCount },
        { count: acceptedCount }
      ] = await Promise.all([
        supabase.from('bookings').select('*', { count: 'exact', head: true }).eq('driver_id', session.user.id),
        supabase.from('chargers').select('*', { count: 'exact', head: true }).eq('owner_id', session.user.id),
        supabase.from('bookings').select('*', { count: 'exact', head: true }).eq('owner_id', session.user.id).eq('status', 'geaccepteerd')
      ])
      setStats({
        bookings: bookingCount || 0,
        chargers: chargerCount || 0,
        accepted: acceptedCount || 0
      })
    } catch (error) {
      // Non-critical
    }
  }, [session.user.id])

  useEffect(() => {
    fetchProfile()
    fetchStats()
  }, [fetchProfile, fetchStats])

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut()
      showToast('Tot ziens! 👋')
      onLogout()
    } catch (error) {
      showToast(t('fout'), true)
    }
  }

  const avatar = profile?.full_name?.[0]?.toUpperCase()
    || session.user.email?.[0]?.toUpperCase()
    || '?'

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '52px 18px 30px' }}>

      <div style={{
        fontFamily: 'Fraunces, serif', fontSize: 24,
        fontWeight: 900, color: 'var(--ink)', marginBottom: 24
      }}>
        {t('profiel')}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--green)', fontWeight: 600 }}>
          {t('laden')}
        </div>
      ) : (
        <>
          {/* Avatar + name */}
          <div style={{
            background: 'white', borderRadius: 20, padding: 20,
            border: '1px solid var(--cream-dark)', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 16
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--green), var(--green-light))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Fraunces, serif', fontWeight: 900,
              fontSize: 26, color: 'white', flexShrink: 0
            }}>
              {avatar}
            </div>
            <div>
              <div style={{
                fontFamily: 'Fraunces, serif', fontSize: 18,
                fontWeight: 900, color: 'var(--ink)', marginBottom: 3
              }}>
                {profile?.full_name || 'Buurtlader gebruiker'}
              </div>
              <div style={{ color: 'var(--ink-muted)', fontSize: 13, marginBottom: 6 }}>
                {session.user.email}
              </div>
              <div style={{
                display: 'inline-block',
                background: 'var(--green-pale)', border: '1px solid var(--green-mid)',
                borderRadius: 20, padding: '2px 10px',
                fontSize: 11, color: 'var(--green)', fontWeight: 700
              }}>
                {ROLE_LABELS[profile?.role] || '🚗 EV-rijder'}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
            gap: 10, marginBottom: 16
          }}>
            {[
              { label: 'Boekingen', value: stats.bookings, icon: '📅' },
              { label: 'Laadpalen', value: stats.chargers, icon: '🔌' },
              { label: 'Geaccepteerd', value: stats.accepted, icon: '✅' },
            ].map(stat => (
              <div key={stat.label} style={{
                background: 'white', borderRadius: 14, padding: '14px 10px',
                textAlign: 'center', border: '1px solid var(--cream-dark)'
              }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</div>
                <div style={{
                  fontFamily: 'Fraunces, serif', fontSize: 22,
                  fontWeight: 900, color: 'var(--green)', marginBottom: 2
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-muted)' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Menu items */}
          <div style={{
            background: 'white', borderRadius: 16, padding: 4,
            border: '1px solid var(--cream-dark)', marginBottom: 16
          }}>
            {[
              {
                icon: '🗺️',
                label: 'Hoe werkt Buurtlader?',
                sublabel: 'Bekijk de introductie opnieuw',
                action: onShowOnboarding
              },
              {
                icon: '🔒',
                label: 'Privacybeleid',
                sublabel: 'laadlink.nl/privacy',
                action: () => window.open('https://laadlink.nl/privacy', '_blank')
              },
              {
                icon: 'ℹ️',
                label: 'Over Buurtlader',
                sublabel: 'Phase 1 Beta · laadlink.nl',
                action: () => window.open('https://laadlink.nl', '_blank')
              },
            ].map((item, i) => (
              <button
                key={i}
                onClick={item.action}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '13px 14px', background: 'none',
                  border: 'none',
                  borderBottom: i < 2 ? '1px solid var(--cream-dark)' : 'none',
                  cursor: 'pointer', textAlign: 'left'
                }}>
                <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{item.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 2 }}>
                    {item.sublabel}
                  </div>
                </div>
                <span style={{ color: 'var(--ink-muted)', fontSize: 18 }}>›</span>
              </button>
            ))}
          </div>

          {/* App version */}
          <div style={{
            textAlign: 'center', fontSize: 11,
            color: 'var(--ink-muted)', marginBottom: 20
          }}>
            Buurtlader Phase 1 Beta · 🇳🇱 Gemaakt in Nederland
          </div>

          {/* Logout */}
          {showLogout ? (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 14, padding: 16, marginBottom: 16
            }}>
              <div style={{
                fontSize: 14, color: 'var(--ink)', fontWeight: 600,
                marginBottom: 12, textAlign: 'center'
              }}>
                {t('uitloggen_bevestig')}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setShowLogout(false)}
                  style={{
                    flex: 1, padding: '11px', borderRadius: 10,
                    border: '1px solid var(--cream-dark)', background: 'white',
                    color: 'var(--ink-muted)', fontWeight: 600, cursor: 'pointer',
                    fontSize: 13, fontFamily: 'DM Sans, sans-serif'
                  }}>
                  Nee, blijf ingelogd
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    flex: 1, padding: '11px', borderRadius: 10,
                    border: 'none', background: 'var(--red)',
                    color: 'white', fontWeight: 700, cursor: 'pointer',
                    fontSize: 13, fontFamily: 'DM Sans, sans-serif'
                  }}>
                  Ja, uitloggen
                </button>
              </div>
            </div>
          ) : (
            <Button fullWidth variant="secondary" onClick={() => setShowLogout(true)}>
              {t('uitloggen')}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

export default ProfilePage
